import { EventSourcePolyfill } from 'event-source-polyfill';
import UserService from './keycloak';
import { apiConfig } from './apiConfig';
import { AMLS_COMMON } from '../pages/constants/globalConstants';

const SSE_PATH = `${AMLS_COMMON}/notifications/events/stream`;

// FIN-13563: event-source-polyfill aborts and reconnects a stream that has been
// silent for `heartbeatTimeout` ms (library default 45 s). The BE only writes a
// ": ping" every SSE_POLL_INTERVAL_S × SSE_HEARTBEAT_TICKS seconds (20 s × 2 by
// default, 20 s × 1 on performance), so with the default a quiet stream was torn
// down and re-opened every ~45 s and any notification landing in that gap never
// toasted. CONSTRAINT: keep this comfortably ABOVE the server ping interval of
// every environment — if an env raises SSE_POLL_INTERVAL_S or SSE_HEARTBEAT_TICKS
// past ~100 s, raise this too (or lower the env's ticks).
export const SSE_HEARTBEAT_TIMEOUT_MS = 120000;

class NotificationStreamService {
  constructor({ onMessage, onError, moduleId }) {
    this.onMessage = onMessage;
    this.onError = onError;
    this.moduleId = moduleId;
    this.eventSource = null;
  }

  connect() {
    this.disconnect();

    const token = UserService.getToken();
    if (!token) return;

    let url = `${apiConfig.serviceApi}${SSE_PATH}`;
    if (this.moduleId) {
      url += `?sourceId=${encodeURIComponent(this.moduleId)}`;
    }

    this.eventSource = new EventSourcePolyfill(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      heartbeatTimeout: SSE_HEARTBEAT_TIMEOUT_MS
    });

    const originalDispatch = this.eventSource.dispatchEvent.bind(
      this.eventSource
    );
    this.eventSource.dispatchEvent = (event) => {
      if (event.type !== 'open' && event.type !== 'error' && event.data) {
        try {
          const data = JSON.parse(event.data);
          this.onMessage?.({ ...data, eventType: event.type });
        } catch (error) {
          console.warn('SSE parse error:', error);
        }
      }
      return originalDispatch(event);
    };

    this.eventSource.onerror = (event) => {
      // EventSourcePolyfill surfaces HTTP status on the error event when a
      // response was received. 4xx is unrecoverable from the client (401 =
      // expired token, 403 = forbidden, 404 = endpoint missing), so stay
      // disconnected instead of hammering the BE every 5s. Network errors
      // and 5xx still get the reconnect.
      const status = event?.status ?? event?.statusCode;
      this.disconnect();
      this.onError?.({ status, event });
      if (status && status >= 400 && status < 500) {
        console.warn(`SSE stopped — HTTP ${status} is not retryable`);
        return;
      }
      this.reconnectTimeout = setTimeout(() => this.connect(), 5000);
    };
  }

  disconnect() {
    clearTimeout(this.reconnectTimeout);
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }
}

export default NotificationStreamService;
